import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Select } from 'primeng/select';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';

import { RegistrationService } from '../../../services/registration.service';
import {
  CheckCompanyNameResponse,
  DropdownOption,
  CompanyMatch,
} from '../../../models/registration.models';
import { CompanySize } from 'src/app/user/model/user';

export interface AccountData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  agreedToTerms: boolean;
  agreedToPrivacy: boolean;
  preferredLanguage?: string;
}

export interface CompanyConflictData {
  company: CompanyMatch;
}

export interface TargetCompany {
  id: string;
  name: string;
}

@Component({
  selector: 'app-register-create',
  templateUrl: './register-create.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Select, TranslocoModule],
})
export class RegisterCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Services
  private registrationService = inject(RegistrationService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);

  // Inputs/Outputs
  @Input() accountData!: AccountData;
  @Output() companyConflict = new EventEmitter<CompanyConflictData>();
  @Output() registrationComplete = new EventEmitter<void>();

  // State
  isLoading = false;
  isCheckingCompanyName = false;
  companyNameCheckResult: CheckCompanyNameResponse | null = null;
  showConflictDialog = false;

  // Form
  companyForm = new FormGroup({
    companyName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
    ]),
    country: new FormControl('', [Validators.required]),
    organisationType: new FormControl('', [Validators.required]),
    size: new FormControl('', [Validators.required]),
    userRole: new FormControl('', [Validators.required]),
    promoCode: new FormControl(''),
    inviteEmails: new FormControl(''),
  });

  // Dropdown options
  countryOptions: DropdownOption[] = [];
  organisationTypeOptions: DropdownOption[] = [];
  employeeCountOptions: DropdownOption[] = [];
  sizeOptions: DropdownOption[] = [];

  ngOnInit(): void {
    // Initialize dropdown options with translations
    this.initializeSizeOptions();

    // Set up real-time company name validation
    this.companyForm
      .get('companyName')
      ?.valueChanges.pipe(debounceTime(700), takeUntil(this.destroy$))
      .subscribe((companyName) => {
        this.checkCompanyNameRealTime(companyName);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Country options
  initializeSizeOptions() {
    const rawOptions = Object.values(CompanySize);
    for (const option of rawOptions) {
      const label = this.translocoService.translate(`companySize.${option}`);
      const value = option;
      this.sizeOptions.push({ label, value });
    }
    this.countryOptions = [
      {
        label: this.translocoService.translate('register.countries.FR'),
        value: 'FR',
      },
      {
        label: this.translocoService.translate('register.countries.BE'),
        value: 'BE',
      },
      {
        label: this.translocoService.translate('register.countries.NL'),
        value: 'NL',
      },
      {
        label: this.translocoService.translate('register.countries.DE'),
        value: 'DE',
      },
      {
        label: this.translocoService.translate('register.countries.GB'),
        value: 'GB',
      },
      {
        label: this.translocoService.translate('register.countries.ES'),
        value: 'ES',
      },
      {
        label: this.translocoService.translate('register.countries.IT'),
        value: 'IT',
      },
      {
        label: this.translocoService.translate('register.countries.OTHER'),
        value: 'OTHER',
      },
    ];

    // Organisation type options
    this.organisationTypeOptions = [
      {
        label: this.translocoService.translate(
          'register.organisationTypes.company',
        ),
        value: 'company',
      },
      {
        label: this.translocoService.translate(
          'register.organisationTypes.travel_agency',
        ),
        value: 'travel_agency',
      },
      {
        label: this.translocoService.translate(
          'register.organisationTypes.nonprofit',
        ),
        value: 'nonprofit',
      },
      {
        label: this.translocoService.translate(
          'register.organisationTypes.government',
        ),
        value: 'government',
      },
      {
        label: this.translocoService.translate(
          'register.organisationTypes.education',
        ),
        value: 'education',
      },
      {
        label: this.translocoService.translate(
          'register.organisationTypes.other',
        ),
        value: 'other',
      },
    ];
  }

  private checkCompanyNameRealTime(companyName: string | null): void {
    // Reset previous results
    this.companyNameCheckResult = null;
    this.showConflictDialog = false;

    if (!companyName || companyName.trim().length < 2) {
      return;
    }

    this.isCheckingCompanyName = true;

    this.registrationService
      .checkCompanyName(companyName.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isCheckingCompanyName = false;
          this.companyNameCheckResult = result;

          // Show conflict dialog if company name is taken
          if (!result.isAvailable && result.matches.length > 0) {
            this.showConflictDialog = true;
          }
        },
        error: (error) => {
          console.error('Company name check failed:', error);
          this.isCheckingCompanyName = false;
          this.companyNameCheckResult = null;
        },
      });
  }

  // Conflict resolution methods
  joinSimilarCompany(): void {
    if (this.companyNameCheckResult?.matches.length) {
      const similarCompany = this.companyNameCheckResult.matches[0];
      this.companyConflict.emit({
        company: similarCompany,
      });
    }
  }

  // Form submission
  onSubmit(): void {
    if (!this.companyForm.valid) {
      this.markFormGroupTouched();
      this.notificationService.warn('Please fill in all required fields.');
      return;
    }

    // Check if there's still a conflict
    if (this.showConflictDialog) {
      this.notificationService.warn(
        'Please resolve the company name conflict before proceeding.',
      );
      return;
    }

    this.isLoading = true;

    const registrationData = {
      // Account data
      email: this.accountData.email,
      password: this.accountData.password,
      firstName: this.accountData.firstName,
      lastName: this.accountData.lastName,
      preferredLanguage: this.accountData.preferredLanguage,
      agreedToTerms: this.accountData.agreedToTerms,
      agreedToPrivacy: this.accountData.agreedToPrivacy,
      phone: '', // Could be collected in future

      // Company data
      companyName: this.companyForm.value.companyName!,
      organisationType: this.companyForm.value.organisationType || undefined,
      size: this.companyForm.value.size || undefined,
      userRole: this.companyForm.value.userRole || undefined,
      promoCode: this.companyForm.value.promoCode || undefined,
      inviteEmails: this.companyForm.value.inviteEmails || undefined,
      companyDetails: {
        vatNumber: '', // Could be added later
        address: {
          street: '',
          streetNumber: '',
          city: '',
          zipCode: '',
          country: this.companyForm.value.country || '',
        },
      },
    };

    this.registrationService
      .selfRegister(registrationData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;

          // Store email for verification flow
          this.registrationService.storePendingVerificationEmail(
            registrationData.email,
          );

          // Emit completion for parent component
          this.registrationComplete.emit();

          // Redirect to email verification pending page
          this.router.navigate(['/auth/email-verification-pending']);
        },
        error: (error) => {
          this.isLoading = false;

          // Capture critical registration failure in Sentry
          Sentry.captureException(error, {
            tags: {
              'registration.failed_step': 'self_register',
              critical_error: 'true',
            },
            contexts: {
              registration: {
                companyName: this.companyForm.value.companyName,
                email: this.accountData.email,
                country: this.companyForm.value.country,
                errorCode: error.error?.code || 'unknown',
              },
            },
          });

          console.error('Registration failed:', error);
          const errorMessage =
            error.userFriendlyMessage ||
            'Registration failed. Please try again.';
          this.notificationService.error(errorMessage);
        },
      });
  }

  // Helper methods
  private markFormGroupTouched(): void {
    Object.keys(this.companyForm.controls).forEach((key) => {
      const control = this.companyForm.get(key);
      control?.markAsTouched();
    });
  }

  getSimilarCompanyName(): string {
    const matches = this.companyNameCheckResult?.matches;
    return matches && matches.length > 0 ? matches[0].name : '';
  }
}
